// SUN-174 mockup 3: on a raw material - every vendor that supplies it, one is preferred.
import { useCallback, useEffect, useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { api } from '@/utils/api';
import { useAuthStore } from '@/stores/auth';
import { LinkWindow } from './LinkWindow';
import { apiError, canEditLinks, fmtLastPo, fmtLead, fmtRate, type ProductLink } from './vendorLinks';

interface Props {
  product: { id: string; name: string; sku?: string | null };
  /** The material row reloads (its Preferred vendor changed). */
  onChanged?: () => void;
}

export function ProductVendorsBlock({ product, onChanged }: Props) {
  const { user } = useAuthStore();
  const editor = canEditLinks(user as any);
  const [links, setLinks] = useState<ProductLink[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<ProductLink | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const res = await api.get(`/vendors/products/by-product/${product.id}`);
      setLinks(res.data?.data?.links ?? res.data?.data ?? []);
    } catch (e: any) {
      setError(apiError(e, 'Could not load the vendors.')); setLinks((l) => l ?? []);
    }
  }, [product.id]);
  useEffect(() => { load(); }, [load]);

  const changed = async () => { await load(); onChanged?.(); };
  const act = async (id: string, fn: () => Promise<unknown>, fail: string) => {
    setBusy(id); setError(null);
    try { await fn(); await changed(); } catch (e: any) { setError(apiError(e, fail)); } finally { setBusy(null); }
  };
  const preferredName = links?.find((l) => l.isPreferred)?.vendor.name ?? null;
  const rows = links ?? [];
  const showRate = editor || rows.some((l) => l.rate !== null && l.rate !== undefined);
  const showLastPrice = editor || rows.some((l) => l.lastPo?.unitPrice !== null && l.lastPo?.unitPrice !== undefined);

  return (
    <div data-testid={`vendors-block-${product.id}`}>
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-900">{product.name} · Vendors</p>
        {editor && <button type="button" onClick={() => setAdding(true)} className="rounded-lg bg-gray-900 px-3 py-1 text-xs font-medium text-white">＋ Add vendor</button>}
      </div>
      {error && <p role="alert" className="mb-2 rounded bg-red-50 px-3 py-1.5 text-xs text-red-700">{error}</p>}
      {links === null ? <p className="text-xs text-gray-500">Loading…</p> : rows.length === 0 ? <p className="text-xs text-gray-500">No vendors linked yet.</p> : (
        <table className="w-full text-xs">
          <thead className="text-left uppercase text-gray-500">
            <tr><th className="pr-4">Vendor</th>{showRate && <th className="pr-4 text-right">Rate ₹</th>}<th className="pr-4">Lead time</th><th className="pr-4">Last PO</th><th /></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((l) => (
              <tr key={l.id}>
                <td className="py-1.5 pr-4 font-medium text-gray-900">{l.vendor.name}</td>
                {showRate && <td className="pr-4 text-right">{fmtRate(l.rate)}</td>}
                <td className="pr-4">{fmtLead(l.leadTimeDays)}</td>
                <td className="pr-4">{fmtLastPo(showLastPrice ? l.lastPo : l.lastPo ? { date: l.lastPo.date } : null)}</td>
                <td className="whitespace-nowrap text-right">
                  {!l.isPreferred && <span className="mr-2 text-xs text-gray-400">2nd choice</span>}
                  {l.isPreferred
                    ? <span className="rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-800">★ Preferred</span>
                    : editor && <button type="button" disabled={busy === l.id} onClick={() => act(l.id, () => api.post(`/vendors/products/${l.id}/preferred`), 'Could not make this vendor preferred.')} className="rounded border border-gray-300 px-2 py-0.5 text-gray-700 hover:bg-gray-50">Make preferred</button>}
                  {editor && (
                    <>
                      <button type="button" title="Edit link" aria-label={`Edit ${l.vendor.name}`} onClick={() => setEditing(l)} className="ml-2 p-1 text-gray-400 hover:text-gray-700"><Pencil size={12} /></button>
                      <button type="button" title="Remove link" aria-label={`Remove ${l.vendor.name}`} disabled={busy === l.id}
                        onClick={() => { if (confirm(`Remove ${l.vendor.name} from ${product.name}? Only the link is removed.`)) act(l.id, () => api.delete(`/vendors/products/${l.id}`), 'Could not remove the link.'); }}
                        className="p-1 text-gray-400 hover:text-red-600"><Trash2 size={12} /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {adding && <LinkWindow pick="vendor" fixed={product} preferredName={preferredName} productHasPreferred={links === null ? undefined : preferredName !== null} onClose={() => setAdding(false)} onSaved={async () => { setAdding(false); await changed(); }} />}
      {editing && (
        <LinkWindow pick="vendor" fixed={{ id: editing.vendorId, name: editing.vendor.name }} edit={{ ...editing, preferredName }}
          onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await changed(); }} />
      )}
    </div>
  );
}
